import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    computed,
    inject,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { environment as env } from '@environment/environment';
import { DashboardWidget } from '@h20-shared/dashboards';
import { DashboardDataService } from '@h20-services/dashboard-data.service';
import { DASHBOARD_WIDGET_MODE } from '../dashboard-widget-mode';
import type { TableauWidgetConfig } from './dashboard-tableau.schema';

type TableauTokenState =
    | { readonly status: 'loading' }
    | { readonly status: 'ready'; readonly token: string }
    | { readonly status: 'error' };

const TOKEN_LOADING: TableauTokenState = { status: 'loading' };

/** An author may paste a whole URL, and Tableau routes its own UI under `/#/views/...`. */
const VIEW_PATH_PREFIX = /^(?:https?:\/\/[^/]+)?\/?(?:#\/)?(?:views\/)?/i;

const INSTANCE_ID = /[?&]:iid=\d+/i;

let nextVizIndex = 0;

const FALLBACK_EMBED_HEIGHT = '800px';

function property(source: unknown, key: string): unknown {
    if (typeof source !== 'object' || source === null || !(key in source)) {
        return undefined;
    }
    try {
        return Reflect.get(source, key);
    } catch {
        return undefined;
    }
}

function authoredHeight(embed: unknown): number | undefined {
    const size = property(property(property(embed, 'workbook'), 'activeSheet'), 'size');
    for (const bound of ['minSize', 'maxSize']) {
        const height = property(property(size, bound), 'height');
        if (typeof height === 'number' && height > 0) {
            return height;
        }
    }
    return undefined;
}

/**
 * Hosts an embedded Tableau view in a Dashboards cell. Self-fetching: it reads its view from
 * `config()` and its token from `DashboardDataService`, so `rows()` and `state()` are never
 * populated for it.
 */
@Component({
    selector: 'pulse-dashboard-tableau',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [TranslateModule],
    host: { '[class.tableau-embedded]': '!authoring' },
    templateUrl: './dashboard-tableau.widget.html',
    styleUrl: './dashboard-tableau.widget.scss',
})
export class DashboardTableauWidget extends DashboardWidget<never, TableauWidgetConfig> {
    private readonly dashboardDataSvc = inject(DashboardDataService);

    protected readonly authoring = inject(DASHBOARD_WIDGET_MODE) === 'authoring';

    protected readonly viewPath = computed(() => {
        const configured = this.config()?.viewUrl.trim() ?? '';
        return configured === ''
            ? undefined
            : configured.replace(VIEW_PATH_PREFIX, '').replace(INSTANCE_ID, '');
    });

    private readonly vizUrl = computed(() => {
        const viewPath = this.viewPath();
        return viewPath === undefined ? undefined : env.awsConfig.Path.tableauUrl + viewPath;
    });

    private readonly tokenState = toSignal(
        this.authoring
            ? of(TOKEN_LOADING)
            : this.dashboardDataSvc.loadTableauToken().pipe(
                  map((response): TableauTokenState => ({
                      status: 'ready',
                      token: response.token,
                  })),
                  catchError(() => of<TableauTokenState>({ status: 'error' }))
              ),
        { initialValue: TOKEN_LOADING }
    );

    protected readonly placeholderPath = computed(() =>
        this.authoring ? this.viewPath() : undefined
    );

    protected readonly embed = computed(() => {
        const vizUrl = this.vizUrl();
        const state = this.tokenState();
        if (this.authoring || vizUrl === undefined || state.status !== 'ready') {
            return undefined;
        }
        return { vizUrl, token: state.token };
    });

    protected readonly showLoading = computed(() => {
        const viewPath = this.viewPath();
        const state = this.tokenState();
        return !this.authoring && viewPath !== undefined && state.status === 'loading';
    });

    protected readonly showError = computed(() => {
        const viewPath = this.viewPath();
        const state = this.tokenState();
        return !this.authoring && viewPath !== undefined && state.status === 'error';
    });

    protected readonly vizId = `Tableau-Viz-${nextVizIndex++}`;

    private readonly viewHeight = signal<number | undefined>(undefined);

    protected readonly embedHeight = computed(() => {
        const height = this.viewHeight();
        return height === undefined ? FALLBACK_EMBED_HEIGHT : `${height}px`;
    });

    protected onFirstInteractive(event: Event): void {
        const height = authoredHeight(event.target);
        if (height !== undefined) {
            this.viewHeight.set(height);
        }
    }
}
